export const demoPlans = [
  "Not sure yet",
  "Website",
  "Social",
  "Growth",
  "Enterprise",
] as const;

export type ValidDemoRequest = {
  id: string;
  name: string;
  email: string;
  business: string;
  plan: (typeof demoPlans)[number];
  preferredAt: string;
  timezone: string;
  goals: string;
};

export function validateDemoRequest(value: unknown, now = Date.now()): ValidDemoRequest {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("Please check your demo details.");
  }
  const data = value as Record<string, unknown>;
  const field = (key: string, min: number, max: number) => {
    if (typeof data[key] !== "string") throw new Error(`Please check your ${key}.`);
    const result = data[key].trim();
    if (
      result.length < min ||
      result.length > max ||
      /[\u0000-\u0008\u000B\u000C\u000E-\u001F]/.test(result)
    ) {
      throw new Error(`Please check your ${key}.`);
    }
    return result;
  };

  const id = field("id", 36, 36);
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id)) {
    throw new Error("Please reopen the form and try again.");
  }
  const name = field("name", 2, 100);
  const email = field("email", 3, 254).toLowerCase();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    throw new Error("Please enter a valid email address.");
  }
  const business = field("business", 2, 150);
  const plan = field("plan", 1, 30);
  if (!(demoPlans as readonly string[]).includes(plan)) throw new Error("Please choose a plan.");
  const preferredAt = field("preferredAt", 20, 30);
  const date = Date.parse(preferredAt);
  if (!Number.isFinite(date) || date <= now || date > now + 366 * 86_400_000) {
    throw new Error("Choose a future date within the next year.");
  }
  const timezone = field("timezone", 1, 100);
  try {
    new Intl.DateTimeFormat("en", { timeZone: timezone }).format();
  } catch {
    throw new Error("Please choose a valid time zone.");
  }
  const goals = field("goals", 0, 1500);
  const website = field("website", 0, 200);
  if (website) throw new Error("Unable to submit this request.");

  return {
    id,
    name,
    email,
    business,
    plan: plan as ValidDemoRequest["plan"],
    preferredAt: new Date(date).toISOString(),
    timezone,
    goals,
  };
}